import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { ObservableField } from 'shared/utils/observer-field';

const PING_INTERVAL_MS = 10_000;
const PONG_TIMEOUT_MS = 5_000;

/**
 * Начало имени топика. Целое имя — с уникальным хвостом на каждый экземпляр: см. `createTopic`.
 */
const CHANNEL_PREFIX = 'overlay_ping_pong';

/**
 * Топик замера — свой у каждой вкладки.
 *
 * Раньше имя было общей константой `overlay_ping_pong`, и все оверлеи мира сидели в одном
 * broadcast-топике: каждый `ping` рассылался всем, каждый получатель отвечал `pong`, и этот
 * `pong` тоже рассылался всем. Стоимость одного пинга — `N + N²` доставок, а пингов в секунду
 * `N/10`, то есть нагрузка на Realtime росла как `N³/10`: 110 сообщений в секунду на десяти
 * клиентах и уже ~12 700 на пятидесяти. Клиентов при этом больше, чем матчей — на устройство
 * открыты оверлей, панель управления и всплывающее окно TV-табло.
 *
 * Со своим топиком у каждой вкладки замер стоит ровно два сообщения на пинг, сколько бы
 * трансляций ни шло одновременно.
 */
function createTopic(): string {
  const unique = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${CHANNEL_PREFIX}_${unique}`;
}

/**
 * Uses a Supabase Realtime broadcast channel to measure round-trip latency
 * (Phoenix-level ping/pong). Sends a "ping" every PING_INTERVAL_MS and waits
 * up to PONG_TIMEOUT_MS for a "pong" echo from the server side.
 *
 * latencyMs — last measured RTT in ms, null when no measurement yet.
 * isAlive   — false when the last ping timed out without a pong.
 */
export class RealtimePingPong {
  latencyMs: ObservableField<number | null> = new ObservableField<number | null>(null);
  isAlive: ObservableField<boolean> = new ObservableField<boolean>(true);

  /** Имя канала этой вкладки. Публичное — по нему замер находят в логах и диагностике. */
  readonly topic = createTopic();

  private channel?: RealtimeChannel;
  private pingTimer?: ReturnType<typeof setInterval>;
  private pongTimer?: ReturnType<typeof setTimeout>;
  private pendingPingId?: string;
  private pendingSentAt?: number;

  constructor(private supabase: SupabaseClient) {}

  public start() {
    this.channel = this.supabase
      .channel(this.topic, { config: { broadcast: { self: true } } })
      .on('broadcast', { event: 'pong' }, ({ payload }) => {
        if (payload?.id !== this.pendingPingId) return;

        clearTimeout(this.pongTimer);
        this.pongTimer = undefined;

        if (this.pendingSentAt !== undefined) {
          this.latencyMs.value = Date.now() - this.pendingSentAt;
        }
        this.isAlive.value = true;
        this.pendingPingId = undefined;
        this.pendingSentAt = undefined;
      })
      .on('broadcast', { event: 'ping' }, ({ payload }) => {
        // Отвечаем только на собственный пинг, вернувшийся через `self: true`. Чужие в свой
        // топик не приходят, но проверка оставлена: без неё возврат общего имени канала молча
        // вернул бы лавину ответов «все всем».
        if (payload?.id !== this.pendingPingId) return;

        // Echo back as pong so the self-broadcast loop completes
        this.channel?.send({ type: 'broadcast', event: 'pong', payload });
      })
      .subscribe((status) => {
        console.debug({ event: 'ping-pong channel', status, topic: this.topic });
        if (status === 'SUBSCRIBED') {
          this.schedulePing();
        }
      });
  }

  public stop() {
    clearInterval(this.pingTimer);
    clearTimeout(this.pongTimer);
    this.pingTimer = undefined;
    this.pongTimer = undefined;
    this.pendingPingId = undefined;
    this.pendingSentAt = undefined;

    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = undefined;
    }
  }

  private schedulePing() {
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL_MS);
    // Send the first ping immediately
    this.sendPing();
  }

  private sendPing() {
    if (!this.channel) return;

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.pendingPingId = id;
    this.pendingSentAt = Date.now();

    this.channel.send({ type: 'broadcast', event: 'ping', payload: { id } });

    this.pongTimer = setTimeout(() => {
      if (this.pendingPingId === id) {
        console.warn({ event: 'ping-pong timeout', id });
        this.isAlive.value = false;
        this.pendingPingId = undefined;
        this.pendingSentAt = undefined;
      }
    }, PONG_TIMEOUT_MS);
  }
}
